import React, { useCallback, useEffect, useState } from 'react';
import {
    Table,
    Button,
    Card,
    Upload,
    message,
    Popconfirm,
    Switch,
    Space,
    Typography,
    Progress,
    Tag,
    Tooltip,
} from 'antd';
import { UploadOutlined, CopyOutlined, DeleteOutlined, ReloadOutlined, ClearOutlined } from '@ant-design/icons';
import {
    getMyUploads,
    uploadFile,
    toggleShareFile,
    deleteUploadedFile,
    getPublicShareUrl,
    cleanupOldFiles,
} from '../services/uploadsService';
import { useAuth } from '../contexts/AuthContext';

const { Text } = Typography;

const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const Uploads = () => {
    const { user } = useAuth();
    const [files, setFiles] = useState([]);
    const [quota, setQuota] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadPercent, setUploadPercent] = useState(0);
    const [cleaning, setCleaning] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getMyUploads();
            setFiles(res.files || []);
            setQuota(res.quota || null);
        } catch (err) {
            message.error('Failed to fetch files');
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleUpload = async (file) => {
        if (quota && quota.remainingToday <= 0) {
            message.error(`Daily upload limit reached (${quota.maxFilesPerDay} files/day)`);
            return Upload.LIST_IGNORE;
        }
        if (quota && file.size > quota.maxFileSizeMB * 1024 * 1024) {
            message.error(`File exceeds the ${quota.maxFileSizeMB}MB limit`);
            return Upload.LIST_IGNORE;
        }

        setUploading(true);
        setUploadPercent(0);
        try {
            await uploadFile(file, (progressEvent) => {
                const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadPercent(percent);
            });
            message.success('File uploaded');
            fetchData();
        } catch (err) {
            message.error(err.response?.data?.message || 'Upload failed');
        }
        setUploading(false);
        return false; // prevent antd's default upload behavior
    };

    const handleToggleShare = async (record, checked) => {
        try {
            const updated = await toggleShareFile(record._id, checked);
            setFiles((prev) => prev.map((f) => (f._id === updated._id ? updated : f)));
            message.success(checked ? 'Sharing enabled' : 'Sharing disabled');
        } catch (err) {
            message.error('Failed to update sharing');
        }
    };

    const handleCopyLink = (record) => {
        const url = getPublicShareUrl(record.shareToken);
        navigator.clipboard.writeText(url);
        message.success('Share link copied to clipboard');
    };

    const handleDelete = async (id) => {
        try {
            await deleteUploadedFile(id);
            message.success('File deleted');
            fetchData();
        } catch (err) {
            message.error('Delete failed');
        }
    };

    const handleCleanupOld = async () => {
        setCleaning(true);
        try {
            const res = await cleanupOldFiles();
            message.success(`Removed ${res.deletedCount} file(s) older than 6 months`);
            fetchData();
        } catch (err) {
            message.error(err.response?.data?.message || 'Cleanup failed');
        }
        setCleaning(false);
    };

    const columns = [
        { title: 'File name', dataIndex: 'originalName', key: 'originalName' },
        { title: 'Size', dataIndex: 'size', key: 'size', render: formatBytes },
        {
            title: 'Uploaded at',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (val) => new Date(val).toLocaleString(),
        },
        {
            title: 'Share',
            key: 'share',
            render: (_, record) => (
                <Space>
                    <Switch
                        checked={record.isShared}
                        onChange={(checked) => handleToggleShare(record, checked)}
                    />
                    {record.isShared && (
                        <Tooltip title="Copy share link">
                            <Button
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={() => handleCopyLink(record)}
                            >
                                Copy link
                            </Button>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        {
            title: 'Downloads',
            dataIndex: 'downloadCount',
            key: 'downloadCount',
            render: (val, record) => (record.isShared ? val : <Tag>private</Tag>),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Popconfirm title="Delete this file?" onConfirm={() => handleDelete(record._id)}>
                    <Button type="link" danger icon={<DeleteOutlined />}>
                        Delete
                    </Button>
                </Popconfirm>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <Card>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 16,
                    }}
                >
                    <h2 style={{ margin: 0 }}>My Files</h2>
                    <Space>
                        {quota && (
                            <Text type="secondary">
                                {quota.remainingToday} of {quota.maxFilesPerDay} uploads left today
                                &nbsp;·&nbsp; max {quota.maxFileSizeMB}MB per file
                            </Text>
                        )}
                        <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
                            Refresh
                        </Button>
                        {user?.role === 'admin' && (
                            <Popconfirm
                                title="Remove all files older than 6 months?"
                                description="This will permanently delete files for all users. This cannot be undone."
                                onConfirm={handleCleanupOld}
                            >
                                <Button icon={<ClearOutlined />} danger loading={cleaning}>
                                    Remove Files &gt; 6 Months
                                </Button>
                            </Popconfirm>
                        )}
                        <Upload
                            beforeUpload={handleUpload}
                            showUploadList={false}
                            disabled={uploading || (quota && quota.remainingToday <= 0)}
                        >
                            <Button type="primary" icon={<UploadOutlined />} loading={uploading}>
                                Upload File
                            </Button>
                        </Upload>
                    </Space>
                </div>

                {uploading && <Progress percent={uploadPercent} size="small" style={{ marginBottom: 16 }} />}

                <Table
                    rowKey="_id"
                    columns={columns}
                    dataSource={files}
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                />
            </Card>
        </div>
    );
};

export default Uploads;
